import { useTranslation } from '@presentation/utils';
import type { FC } from 'react';
import { useMemo } from 'react';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';
import { FaCheck } from 'react-icons/fa';
import { TiWarning } from 'react-icons/ti';
import type { QuizPageProps } from '..';
import { assets } from './assets';
import bubbleLeft from './assets/bulle-left.svg';
import bubbleRight from './assets/bulle-right.svg';
import topDecoration from './assets/decoration.svg';
import dogDecoration from './assets/questionnaire-3.webp';
import * as Styled from './styled';

export * from './types';

export const QuizPage: FC<QuizPageProps> = (props) => {
  const { currentStep, steps, onNextClick, onPreviousClick, onAnswerSelect, answers } = props;

  const { translate } = useTranslation();

  const maxSteps = useMemo(() => steps.length - 1, [steps]);
  const step = useMemo(() => steps[currentStep], [currentStep, steps]);
  const hasAnswer = useMemo(() => answers[currentStep] !== undefined, [answers, currentStep]);

  const messages: Record<number, string> = useMemo(
    () => ({
      0: translate('quiz.general.dogMessage1'),
      3: translate('quiz.general.dogMessage2'),
      7: translate('quiz.general.dogMessage3'),
      9: translate('quiz.general.dogMessage4'),
    }),
    [translate],
  );
  const dogInteractionMessage = useMemo((): string | undefined => {
    if (!hasAnswer) return;

    return messages[currentStep] ?? undefined;
  }, [currentStep, hasAnswer, messages]);

  return (
    <>
      <Styled.HeadWrapper>
        <Styled.Title>{translate('quiz.general.title')}</Styled.Title>
        <Styled.Subtitle>{translate('quiz.general.subtitle')}</Styled.Subtitle>
      </Styled.HeadWrapper>
      <Styled.ContentWrapper>
        <Styled.TopDecoration src={topDecoration} />
        {currentStep !== 3 && (
          <Styled.DogDecorationWrapper right={currentStep > 6}>
            <img src={dogDecoration} alt="dog" />
            <Styled.DogInteraction right={currentStep > 6} active={!!dogInteractionMessage}>
              <img src={currentStep > 6 ? bubbleRight : bubbleLeft} alt="interaction" />
              {!!dogInteractionMessage && (
                <Styled.DogInteractionText>{dogInteractionMessage}</Styled.DogInteractionText>
              )}
            </Styled.DogInteraction>
          </Styled.DogDecorationWrapper>
        )}
        <Styled.ContentContainer>
          <>
            <Styled.QuestionTitle id="quiz-question-title">{step.question}</Styled.QuestionTitle>

            <Styled.AnswerContainer>
              {step.propositions.map((proposition, index) => {
                let asset;
                const isActive = hasAnswer && answers[currentStep] === proposition.value;
                if ([5, 6, 9, 10].indexOf(index + 1) < 0) {
                  asset =
                    assets[
                      `step${currentStep + 1}Proposition${index + 1}${
                        isActive ? 'Active' : ''
                      }` as keyof typeof assets
                    ];
                }

                return (
                  <>
                    <Styled.Answer
                      itemAmount={step.propositions.length}
                      active={isActive}
                      noImage={!asset}
                      onClick={() => onAnswerSelect?.(proposition.value)}
                    >
                      {asset && <Styled.AnswerImage active={isActive} src={asset} alt="answer" />}
                      <Styled.AnswerCheckWrapper active={isActive}>
                        <FaCheck />
                      </Styled.AnswerCheckWrapper>
                      <Styled.AnswerText
                        noImage={!asset}
                        dangerouslySetInnerHTML={{ __html: proposition.label?.toString() ?? '' }}
                      />
                    </Styled.Answer>
                  </>
                );
              })}
            </Styled.AnswerContainer>

            <Styled.SkipWrapper>
              {step.skippable && (
                <Styled.SkipText
                  active={hasAnswer && answers[currentStep] === null}
                  onClick={(event) => {
                    onAnswerSelect?.(null);
                  }}
                >
                  {translate('quiz.general.skip')}
                </Styled.SkipText>
              )}
            </Styled.SkipWrapper>

            <Styled.NavigationContainer>
              <Styled.NavigationArrow
                disabled={currentStep === 0}
                onClick={(event) => currentStep > 0 && onPreviousClick?.(event)}
              >
                <BsChevronLeft />
              </Styled.NavigationArrow>

              <Styled.NavigationText>
                {translate('quiz.general.step')
                  .replace('$step', (currentStep + 1).toString())
                  .replace('$total', (maxSteps + 1).toString())}
              </Styled.NavigationText>

              <Styled.NavigationArrow
                disabled={!hasAnswer}
                active={hasAnswer}
                onClick={(event) => {
                  if (!hasAnswer) return;
                  onNextClick?.(event);
                }}
              >
                <BsChevronRight />
              </Styled.NavigationArrow>
            </Styled.NavigationContainer>

            {!!step.tip && (
              <Styled.HelpWrapper>
                <Styled.HelpIcon>
                  <TiWarning />
                </Styled.HelpIcon>
                {step.tip}
              </Styled.HelpWrapper>
            )}
          </>
        </Styled.ContentContainer>
      </Styled.ContentWrapper>
    </>
  );
};
